import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { SIGNATURE_SCRIPT_FONTS } from '@/data/pdfTools';
import {
  canvasToPngBlob,
  renderTextSignature,
} from '@/lib/pdf/signatureRaster';

export interface WriteSignaturePadHandle {
  isEmpty: () => boolean;
  toPngBlob: () => Promise<Blob>;
  getName: () => string;
}

interface WriteSignaturePadProps {
  color: string;
  onDirtyChange?: (dirty: boolean) => void;
}

const WriteSignaturePad = forwardRef<WriteSignaturePadHandle, WriteSignaturePadProps>(
  function WriteSignaturePad({ color, onDirtyChange }, ref) {
    const [text, setText] = useState('');
    const [fontId, setFontId] = useState<(typeof SIGNATURE_SCRIPT_FONTS)[number]['id']>(
      'caveat'
    );

    const font =
      SIGNATURE_SCRIPT_FONTS.find((f) => f.id === fontId) || SIGNATURE_SCRIPT_FONTS[0];

    useEffect(() => {
      onDirtyChange?.(text.trim().length > 0);
    }, [text, onDirtyChange]);

    useImperativeHandle(ref, () => ({
      isEmpty: () => !text.trim(),
      getName: () => text.trim() || 'Signature',
      toPngBlob: async () => {
        const canvas = renderTextSignature({
          text,
          fontFamily: font.family,
          color,
        });
        return canvasToPngBlob(canvas);
      },
    }));

    return (
      <div className="pdfe-sig-write">
        <label className="pdfe-field">
          <span>Your name</span>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your signature"
            maxLength={80}
          />
        </label>
        <label className="pdfe-field">
          <span>Font</span>
          <select
            value={fontId}
            onChange={(e) =>
              setFontId(e.target.value as (typeof SIGNATURE_SCRIPT_FONTS)[number]['id'])
            }
          >
            {SIGNATURE_SCRIPT_FONTS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <div className="pdfe-sig-write-preview" aria-live="polite">
          {text.trim() ? (
            <span style={{ color, fontFamily: font.family, fontSize: '2.4rem' }}>
              {text}
            </span>
          ) : (
            <span className="pdfe-sig-write-placeholder">Preview appears here</span>
          )}
        </div>
      </div>
    );
  }
);

export default WriteSignaturePad;
